import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Leaf, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Update user context
        updateUser(prev => ({ ...prev, is_paid_member: true }));
        toast.success('Welcome to Twig! Your membership is now active.');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full mx-auto p-8 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
            <Leaf className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-3xl font-serif font-semibold">Twig</span>
        </div>

        {status === 'checking' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
            <h1 className="text-2xl font-serif font-semibold">Confirming your payment...</h1>
            <p className="text-muted-foreground">Please wait while we verify your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-serif font-semibold">Welcome to Twig!</h1>
            <p className="text-muted-foreground">
              Your membership is now active. You have full access to discover matches, 
              take quizzes, and connect with others.
            </p>
            <Button 
              onClick={() => navigate('/onboarding')}
              className="w-full rounded-xl h-12"
              data-testid="continue-btn"
            >
              Continue to Setup
            </Button>
          </div>
        )}

        {status === 'expired' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-serif font-semibold text-destructive">Payment Expired</h1>
            <p className="text-muted-foreground">
              Your payment session has expired. Please try again.
            </p>
            <Button 
              onClick={() => navigate('/payment')}
              className="w-full rounded-xl h-12"
            >
              Try Again
            </Button>
          </div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <div className="space-y-6">
            <h1 className="text-2xl font-serif font-semibold">Payment Status Unknown</h1>
            <p className="text-muted-foreground">
              We couldn't confirm your payment status. If you were charged, 
              your membership will be activated shortly. Please check your email for confirmation.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/payment')}
                className="flex-1 rounded-xl"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="flex-1 rounded-xl"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
